import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSidebar } from "@/components/ui/sidebar";
import { useAppDispatch, useAppSelector } from "@/stores/hook";
import { selectIsTenantAdmin } from "@/rbac/rbacSlice";
import { isLeadManagementIndustry } from "@/rbac/industry";
import useCurrentIndustry from "@/hooks/globalapis/useCurrentIndustry";
import { useIsImpersonating } from "@/features/auth/impersonation/ImpersonationBanner";
import { setIsAddLead } from "@/features/leads/stores/leadFunctionalSlice";
import { buildTourSteps, sidebarLink, type TourStep } from "./tourSteps";
import TourOverlay from "./TourOverlay";
import { findVisibleTarget } from "./tourDom";
import GettingStartedChecklist from "./GettingStartedChecklist";
import { QUESTS, type Quest } from "./quests";
import { OnboardingTourContext } from "./useOnboardingTour";
import {
  isNewWorkspace,
  readOnboardingState,
  writeOnboardingState,
  type OnboardingState,
} from "./onboardingStorage";

/**
 * The welcome tour + getting-started checklist (owner, 2026-11-04: "guide
 * them across the CRM… simple and funny").
 *
 * WHO SEES IT. On its own: everyone in a workspace younger than 30 days, once
 * per browser, on a desktop-sized screen, never while "Viewing as" someone.
 * The checklist: that workspace's tenant admins, after the tour. Anyone can
 * replay the tour from the profile menu. Workspaces older than 30 days see
 * nothing unless they ask, so existing customers' screens are unchanged.
 */

/** How long to wait for a step's element before skipping the step. */
const WAIT_MS = 700;
const WAIT_AFTER_ROUTE_MS = 4000;

export function OnboardingTourProvider({ children }: { children: ReactNode }) {
  const user = useAppSelector((state) => state.auth.currentUser?.user);
  const isTenantAdmin = useAppSelector(selectIsTenantAdmin);
  const isAddLead = useAppSelector((state) => Boolean(state.leadFunctional.isAddLead));
  const impersonating = useIsImpersonating();
  const { industry } = useCurrentIndustry();
  const { open: sidebarOpen, setOpen: setSidebarOpen, isMobile } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const userId: number | undefined = user?.id;
  const newWorkspace = isNewWorkspace(user?.tenant?.createdAt);
  const rawFirstName = String(user?.name ?? "").trim().split(/\s+/)[0] ?? "";
  const firstName = rawFirstName === "System" ? "" : rawFirstName;
  const isLeadManagement = isLeadManagementIndustry(industry);

  // ── Remembered progress (per user, this browser) ──────────────────
  // Read once: the layout remounts on sign-in, and "Switch user" reloads.
  const [saved, setSaved] = useState<OnboardingState>(() => readOnboardingState(userId));
  const save = useCallback(
    (patch: (prev: OnboardingState) => OnboardingState) => {
      setSaved((prev) => {
        const next = patch(prev);
        writeOnboardingState(userId, next);
        return next;
      });
    },
    [userId]
  );
  const tickQuest = useCallback(
    (id: string) =>
      save((prev) =>
        prev.quests?.includes(id) ? prev : { ...prev, quests: [...(prev.quests ?? []), id] }
      ),
    [save]
  );

  // ── Tour state ─────────────────────────────────────────────────────
  const [steps, setSteps] = useState<TourStep[] | null>(null);
  const [index, setIndex] = useState(0);
  // The element found for a step. A step with no target needs none.
  const [resolved, setResolved] = useState<{ index: number; el: HTMLElement } | null>(null);
  const directionRef = useRef<1 | -1>(1);
  const wasSidebarOpenRef = useRef(false);
  const startPathRef = useRef<string>("/");
  const pathRef = useRef(location.pathname);
  useLayoutEffect(() => {
    pathRef.current = location.pathname;
  }, [location.pathname]);
  const active = steps != null;

  const [questsOpen, setQuestsOpen] = useState(false);

  const startTour = useCallback(() => {
    if (steps) return;
    startPathRef.current = pathRef.current;
    wasSidebarOpenRef.current = sidebarOpen && !isMobile;
    // The icon rail shows every link at once, so collapse to it for the tour.
    if (wasSidebarOpenRef.current) setSidebarOpen(false);
    setQuestsOpen(false);
    window.setTimeout(() => {
      // Only doors this person can open: a step whose element isn't on screen
      // (no such tab for the role, tool not connected) is left out.
      const all = buildTourSteps({ firstName, isLeadManagement });
      const available = all.filter((step) => {
        if (!step.target) return true;
        if (step.route) return findVisibleTarget(sidebarLink(step.route)) != null;
        return findVisibleTarget(step.target) != null;
      });
      directionRef.current = 1;
      setIndex(0);
      setResolved(null);
      setSteps(available);
    }, wasSidebarOpenRef.current ? 350 : 0);
  }, [steps, sidebarOpen, isMobile, setSidebarOpen, firstName, isLeadManagement]);

  const endTour = useCallback(
    (how: "skip" | "explore" | "addLead") => {
      setSteps(null);
      setResolved(null);
      save((prev) => ({
        ...prev,
        tourSeen: true,
        quests:
          how === "skip" || prev.quests?.includes("tour")
            ? prev.quests
            : [...(prev.quests ?? []), "tour"],
      }));
      if (wasSidebarOpenRef.current) setSidebarOpen(true);
      if (how === "addLead") {
        navigate("/leads");
        dispatch(setIsAddLead(true));
      } else if (pathRef.current !== startPathRef.current) {
        navigate(startPathRef.current);
      }
      if (how !== "addLead") setQuestsOpen(true);
    },
    [save, setSidebarOpen, navigate, dispatch]
  );

  // The step effect below reads these through refs: the router hands out a
  // new `navigate` after a route change, and re-running the effect then would
  // restart the wait with the short timeout and skip a page that is loading.
  const navigateRef = useRef(navigate);
  const endTourRef = useRef(endTour);
  useLayoutEffect(() => {
    navigateRef.current = navigate;
    endTourRef.current = endTour;
  }, [navigate, endTour]);

  const go = useCallback(
    (delta: 1 | -1) => {
      if (!steps) return;
      directionRef.current = delta;
      const next = index + delta;
      if (next < 0) return;
      if (next >= steps.length) {
        endTour("explore");
        return;
      }
      setIndex(next);
    },
    [steps, index, endTour]
  );

  // Resolve the current step: open its page, then wait for its element.
  // Missing after the wait → skip it in the direction we were going.
  useEffect(() => {
    if (!steps) return;
    const step = steps[index];
    if (!step?.target) return;
    const routed = Boolean(step.route && pathRef.current !== step.route);
    if (routed) navigateRef.current(step.route!);
    const started = Date.now();
    let timer = 0;
    let cancelled = false;
    const poll = () => {
      if (cancelled) return;
      const el = findVisibleTarget(step.target!);
      if (el) {
        el.scrollIntoView({ block: "nearest", inline: "nearest" });
        setResolved({ index, el });
        return;
      }
      if (Date.now() - started > (routed ? WAIT_AFTER_ROUTE_MS : WAIT_MS)) {
        const next = index + directionRef.current;
        if (next < 0) setIndex(0);
        else if (next >= steps.length) endTourRef.current("explore");
        else setIndex(next);
        return;
      }
      timer = window.setTimeout(poll, 100);
    };
    timer = window.setTimeout(poll, routed ? 150 : 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [steps, index]);

  // Arrow keys walk, Escape leaves.
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") endTour("skip");
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, go, endTour]);

  // ── Start on its own, once, in a new workspace ────────────────────
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (autoStartedRef.current || active) return;
    // Wait for the industry so the stage card can name this pipeline's stages.
    if (!userId || !newWorkspace || impersonating || saved.tourSeen || !industry) return;
    if (isMobile || window.innerWidth < 1024) return;
    const timer = window.setTimeout(() => {
      autoStartedRef.current = true;
      startTour();
    }, 1500);
    return () => window.clearTimeout(timer);
  }, [userId, newWorkspace, impersonating, saved.tourSeen, industry, isMobile, active, startTour]);

  // ── Checklist: tick quests as their pages are opened ──────────────
  const showChecklist =
    isTenantAdmin &&
    newWorkspace &&
    !impersonating &&
    !isMobile &&
    !active &&
    Boolean(saved.tourSeen) &&
    !saved.questsDismissed;

  useEffect(() => {
    if (!newWorkspace) return;
    const path = location.pathname;
    for (const quest of QUESTS) {
      if (!quest.path || quest.id === "first-lead") continue;
      if (path === quest.path || path.startsWith(`${quest.path}/`)) tickQuest(quest.id);
    }
  }, [location.pathname, newWorkspace, tickQuest]);

  useEffect(() => {
    if (isAddLead && newWorkspace && !active) tickQuest("first-lead");
  }, [isAddLead, newWorkspace, active, tickQuest]);

  const doneQuests = useMemo(() => new Set(saved.quests ?? []), [saved.quests]);

  const pickQuest = useCallback(
    (quest: Quest) => {
      if (quest.id === "tour") {
        startTour();
        return;
      }
      if (quest.id === "first-lead") {
        navigate("/leads");
        dispatch(setIsAddLead(true));
        setQuestsOpen(false);
        return;
      }
      if (quest.path) navigate(quest.path);
      setQuestsOpen(false);
    },
    [startTour, navigate, dispatch]
  );

  const value = useMemo(() => ({ startTour }), [startTour]);
  const step = steps?.[index];
  const target = step?.target && resolved?.index === index ? resolved.el : null;
  const ready = Boolean(step && (!step.target || target));
  // The counter and progress bar count only the cards between hello and bye.
  const leadIn = steps?.[0]?.id === "welcome" ? 1 : 0;
  const middleTotal = steps
    ? steps.filter((s) => s.id !== "welcome" && s.id !== "done").length
    : 0;

  return (
    <OnboardingTourContext.Provider value={value}>
      {children}
      {step && ready && (
        <TourOverlay
          step={step}
          target={target}
          position={index - leadIn}
          total={middleTotal}
          onNext={() => go(1)}
          onBack={() => go(-1)}
          onSkip={() => endTour("skip")}
          onFinish={(choice) => endTour(choice)}
        />
      )}
      {showChecklist && (
        <GettingStartedChecklist
          quests={QUESTS}
          done={doneQuests}
          open={questsOpen}
          onToggle={() => setQuestsOpen((v) => !v)}
          onPick={pickQuest}
          onDismiss={() => save((prev) => ({ ...prev, questsDismissed: true }))}
        />
      )}
    </OnboardingTourContext.Provider>
  );
}
